import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Edit, Trash2, X, BadgePercent, Gift, Building2, School, Globe, Users, Filter, Search } from 'lucide-react';

const INSTITUTION_TYPES = [
    { value: 'all', label: 'All Types' },
    { value: 'international_school', label: 'International School', icon: Globe },
    { value: 'private_school', label: 'Private School', icon: Building2 },
    { value: 'government_public_school', label: 'Government/Public School', icon: School },
    { value: 'sports_academy', label: 'Sports Academy', icon: Users },
    { value: 'community_sports_club', label: 'Community Sports Club', icon: Users }
];

const SchoolsTab = ({ 
    schoolRankings,
    selectedSchools,
    onSelectAll,
    onSelectSchool,
    onEdit,
    onDelete,
    onCreate,
    onBulkDelete,
    onClearSelection,
    sports = []  // Add sports prop
}) => {
    const [activeSubTab, setActiveSubTab] = useState('all'); // 'all' | 'fee_waiver'
    const [institutionTypeFilter, setInstitutionTypeFilter] = useState('all');
    const [sportFilter, setSportFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Filter schools based on active sub-tab, institution type, and sport
    const filteredSchools = useMemo(() => {
        let filtered = schoolRankings;
        
        // Filter by sub-tab (Fee Waiver shows only schools with waivers)
        if (activeSubTab === 'fee_waiver') {
            filtered = filtered.filter(s => s.fee_waiver_type && s.fee_waiver_type !== 'none');
        }
        
        // Filter by institution type
        if (institutionTypeFilter !== 'all') {
            filtered = filtered.filter(s => s.institution_type === institutionTypeFilter);
        }
        
        // Filter by sport (check both sports array and legacy sport field)
        if (sportFilter !== 'all') {
            filtered = filtered.filter(s => {
                if (s.sports && Array.isArray(s.sports)) {
                    return s.sports.includes(sportFilter);
                }
                return s.sport === sportFilter;
            });
        }
        
        // Filter by search query
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter(s =>
                s.school_name?.toLowerCase().includes(q) ||
                s.city?.toLowerCase().includes(q) ||
                s.country?.toLowerCase().includes(q)
            );
        }
        
        return filtered;
    }, [schoolRankings, activeSubTab, institutionTypeFilter, sportFilter, searchQuery]);

    // Count schools with waivers for badge
    const waiverCount = useMemo(() => {
        return schoolRankings.filter(s => s.fee_waiver_type && s.fee_waiver_type !== 'none').length;
    }, [schoolRankings]);

    // Helper function to render waiver badge
    const renderWaiverBadge = (school) => {
        if (!school.fee_waiver_type || school.fee_waiver_type === 'none') return null;
        
        if (school.fee_waiver_type === 'full') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full" data-testid={`waiver-badge-${school.id}`}>
                    <Gift className="w-3 h-3" />
                    100% Free
                </span>
            );
        }
        
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full" data-testid={`waiver-badge-${school.id}`}>
                <BadgePercent className="w-3 h-3" />
                -{school.fee_waiver_amount || 0} KSH
            </span>
        );
    };

    // Helper function to render institution type badge
    const renderInstitutionBadge = (school) => {
        const type = INSTITUTION_TYPES.find(t => t.value === school.institution_type);
        if (!type || type.value === 'all') {
            return <span className="text-muted-foreground text-xs">Sports Academy</span>;
        }
        
        const IconComponent = type.icon;
        const colorClass = {
            'international_school': 'bg-purple-500/20 text-purple-400',
            'private_school': 'bg-blue-500/20 text-blue-400',
            'government_public_school': 'bg-emerald-500/20 text-emerald-400',
            'sports_academy': 'bg-orange-500/20 text-orange-400',
            'community_sports_club': 'bg-pink-500/20 text-pink-400'
        }[school.institution_type] || 'bg-gray-500/20 text-gray-400';
        
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${colorClass} text-xs rounded-full`} data-testid={`institution-badge-${school.id}`}>
                <IconComponent className="w-3 h-3" />
                {type.label}
            </span>
        );
    };

    return (
        <div className="space-y-4">
            {/* Sub-tabs: All Schools | Fee Waiver */}
            <div className="flex items-center gap-2 border-b border-white/10 pb-2">
                <button
                    onClick={() => setActiveSubTab('all')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                        activeSubTab === 'all' 
                            ? 'text-primary border-b-2 border-primary' 
                            : 'text-muted-foreground hover:text-white'
                    }`}
                    data-testid="schools-subtab-all"
                >
                    All Schools
                    <span className="ml-2 px-2 py-0.5 bg-white/10 rounded-full text-xs">
                        {schoolRankings.length}
                    </span>
                </button>
                <button
                    onClick={() => setActiveSubTab('fee_waiver')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                        activeSubTab === 'fee_waiver' 
                            ? 'text-primary border-b-2 border-primary' 
                            : 'text-muted-foreground hover:text-white'
                    }`}
                    data-testid="schools-subtab-fee-waiver"
                >
                    Fee Waiver
                    {waiverCount > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-xs">
                            {waiverCount}
                        </span>
                    )}
                </button>
            </div>

            {/* Filters and Actions Bar */}
            <div className="flex flex-wrap gap-4 p-4 bg-[#0d2847] border border-white/5 items-center">
                {/* Search */}
                <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by name, city or country..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 bg-[#0a1628] border-white/10 h-9"
                        data-testid="schools-search-input"
                    />
                </div>

                {/* Institution Type Filter */}
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <Select value={institutionTypeFilter} onValueChange={setInstitutionTypeFilter}>
                        <SelectTrigger className="w-[200px] bg-[#0a1628] border-white/10 h-9" data-testid="institution-type-filter">
                            <SelectValue placeholder="Filter by type" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0d2847] border-white/10">
                            {INSTITUTION_TYPES.map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Sport Filter */}
                <div className="flex items-center gap-2">
                    <Select value={sportFilter} onValueChange={setSportFilter}>
                        <SelectTrigger className="w-[160px] bg-[#0a1628] border-white/10 h-9" data-testid="sport-filter">
                            <SelectValue placeholder="Filter by sport" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0d2847] border-white/10 max-h-64">
                            <SelectItem value="all">All Sports</SelectItem>
                            {sports.map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Bulk Actions for Schools */}
                {selectedSchools.length > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30">
                        <span className="text-sm text-primary font-medium">
                            {selectedSchools.length} selected
                        </span>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onBulkDelete}
                            className="text-destructive hover:text-destructive hover:bg-destructive/20 h-7 text-xs"
                            data-testid="bulk-delete-schools-btn"
                        >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onClearSelection}
                            className="text-muted-foreground hover:text-white h-7 text-xs"
                        >
                            <X className="w-3 h-3" />
                        </Button>
                    </div>
                )}
                
                <span className="text-sm text-muted-foreground flex-1 text-right">
                    {filteredSchools.length} {activeSubTab === 'fee_waiver' ? 'schools with waiver' : 'schools'} 
                    {(institutionTypeFilter !== 'all' || sportFilter !== 'all') && ` (filtered)`}
                </span>
                <Button
                    onClick={onCreate}
                    className="bg-primary hover:bg-cyan-400 text-[#0a1628]"
                    data-testid="create-school-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add School
                </Button>
            </div>

            {/* Schools Table */}
            <div className="bg-[#0d2847] border border-white/5 overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-[#0d2847]">
                        <tr>
                            <th className="text-left p-4 w-12">
                                <input
                                    type="checkbox"
                                    checked={filteredSchools.length > 0 && filteredSchools.every(s => selectedSchools.includes(s.id))}
                                    onChange={(e) => onSelectAll(e.target.checked)}
                                    className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                                    data-testid="select-all-schools-checkbox"
                                />
                            </th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Best Rank</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">School</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Type</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Location</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Sports & Rankings</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Record</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Total Pts</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Waiver</th>
                            <th className="text-right text-xs tracking-widest uppercase text-muted-foreground p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredSchools.map((school) => (
                            <tr key={school.id} className={`border-t border-white/5 hover:bg-white/5 ${selectedSchools.includes(school.id) ? 'bg-primary/5' : ''}`}>
                                <td className="p-4">
                                    <input
                                        type="checkbox"
                                        checked={selectedSchools.includes(school.id)}
                                        onChange={(e) => onSelectSchool(school.id, e.target.checked)}
                                        className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                                        data-testid={`select-school-${school.id}`}
                                    />
                                </td>
                                <td className="p-4">
                                    {/* Calculate best rank from sport_rankings */}
                                    {(() => {
                                        let bestRank = school.rank || 999;
                                        if (school.sport_rankings && Object.keys(school.sport_rankings).length > 0) {
                                            Object.values(school.sport_rankings).forEach(sr => {
                                                if (sr.rank && sr.rank < bestRank) {
                                                    bestRank = sr.rank;
                                                }
                                            });
                                        }
                                        return (
                                            <span className={`stat-display text-xl ${bestRank < 4 ? 'text-accent' : bestRank < 10 ? 'text-primary' : 'text-muted-foreground'}`}>
                                                {bestRank < 999 ? `#${bestRank}` : '-'}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td className="p-4 font-medium">{school.school_name}</td>
                                <td className="p-4">
                                    {renderInstitutionBadge(school)}
                                </td>
                                <td className="p-4 text-sm text-muted-foreground">{school.city}, {school.country}</td>
                                <td className="p-4 text-sm">
                                    {/* Display sports with their ranks */}
                                    {school.sports && school.sports.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {school.sports.slice(0, 3).map((s, i) => {
                                                const sportRank = school.sport_rankings?.[s];
                                                return (
                                                    <span 
                                                        key={i} 
                                                        className="px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded flex items-center gap-1"
                                                        title={sportRank ? `Rank: #${sportRank.rank}, Points: ${sportRank.points}, Record: ${sportRank.wins}W-${sportRank.losses}L` : s}
                                                    >
                                                        {s}
                                                        {sportRank && sportRank.rank && sportRank.rank < 999 && (
                                                            <span className="text-accent font-bold">#{sportRank.rank}</span>
                                                        )}
                                                    </span>
                                                );
                                            })}
                                            {school.sports.length > 3 && (
                                                <span className="px-1.5 py-0.5 bg-white/10 text-muted-foreground text-xs rounded">
                                                    +{school.sports.length - 3} more
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded">
                                            {school.sport || 'N/A'}
                                        </span>
                                    )}
                                </td>
                                <td className="p-4 text-sm">
                                    {/* Calculate total wins/losses from sport_rankings or use legacy fields */}
                                    {(() => {
                                        let totalWins = 0;
                                        let totalLosses = 0;
                                        if (school.sport_rankings && Object.keys(school.sport_rankings).length > 0) {
                                            Object.values(school.sport_rankings).forEach(sr => {
                                                totalWins += sr.wins || 0;
                                                totalLosses += sr.losses || 0;
                                            });
                                        } else {
                                            totalWins = school.wins || 0;
                                            totalLosses = school.losses || 0;
                                        }
                                        return (
                                            <>
                                                <span className="text-success">{totalWins}W</span>
                                                <span className="text-muted-foreground mx-1">-</span>
                                                <span className="text-destructive">{totalLosses}L</span>
                                            </>
                                        );
                                    })()}
                                </td>
                                <td className="p-4">
                                    {/* Calculate total points from sport_rankings or use legacy field */}
                                    {(() => {
                                        let totalPoints = 0;
                                        if (school.sport_rankings && Object.keys(school.sport_rankings).length > 0) {
                                            Object.values(school.sport_rankings).forEach(sr => {
                                                totalPoints += sr.points || 0;
                                            });
                                        } else {
                                            totalPoints = school.points || 0;
                                        }
                                        return <span className="stat-display text-xl text-primary">{totalPoints}</span>;
                                    })()}
                                </td>
                                <td className="p-4">
                                    {renderWaiverBadge(school) || <span className="text-muted-foreground text-xs">-</span>}
                                </td>
                                <td className="p-4">
                                    <div className="flex items-center justify-end gap-1">
                                        <Button size="sm" variant="ghost" onClick={() => onEdit(school)} className="text-muted-foreground hover:text-white" data-testid={`edit-school-${school.id}`}>
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => onDelete(school.id)} className="text-destructive hover:text-destructive" data-testid={`delete-school-${school.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredSchools.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                        {activeSubTab === 'fee_waiver' 
                            ? 'No schools with fee waivers found' 
                            : 'No school rankings found'
                        }
                    </div>
                )}
            </div>
        </div>
    );
};

export default SchoolsTab;
